'use client';

import { useMemo } from 'react';
import { type Dispatch, type SetStateAction } from 'react';
import { Calendar, Users, Info } from 'lucide-react';
import type { ExcursionAsset } from '@/components/booking/UnifiedBookingModal';
import type { ExcursionWizardState } from '@/components/booking/excursion/ExcursionWizard';
import { resolveExcursionExtras } from '@/lib/excursion-extras';
import { computeExcursionPricing } from '@/lib/excursion-wizard-pricing';

/* ── Helpers ─────────────────────────────────────────────────────────────────── */

/** Applica markup su un prezzo unitario e arrotonda all'euro intero (identico al helper). */
function applyMarkup(vendorPrice: number, rate: number): number {
  return Math.round(vendorPrice * (1 + rate / 100));
}

function fmtCur(n: number): string {
  return Math.round(n).toLocaleString('it-IT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function fmtDate(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/* ── Props ──────────────────────────────────────────────────────────────────── */

interface StepSummaryProps {
  asset: ExcursionAsset;
  state: ExcursionWizardState;
  setState: Dispatch<SetStateAction<ExcursionWizardState>>;
}

/* ── Component ──────────────────────────────────────────────────────────────── */

export default function StepSummary({ asset, state }: StepSummaryProps) {
  const markupRate = asset.markupRate ?? 0;
  const people     = state.participants;

  /* ── Calcolo completo via helper condiviso ───────────────────────────────────
   * Fonte unica per total, customerOnline, customerOffline, lmDiscount, ecc.
   * StepSummary non ricalcola le cifre: le riceve tutte da computeExcursionPricing.
   * Questo garantisce che display e importo addebitato coincidano.
   */
  const pricing = useMemo(
    () => computeExcursionPricing(asset, state),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [asset, state],
  );

  /* ── Tier selezionato (solo per la label di display) ─────────────────────────
   * Mirror di ExcursionWizard/StepParticipants.
   */
  const selectedTier = useMemo(() => {
    if (!state.tierKey) return null;
    if (state.tierKey === 'exclusive') {
      if (!asset.exclusivePrice) return null;
      return {
        label: asset.exclusiveLabel ?? 'Esclusiva — tutta la barca',
        exclusive: true as const,
      };
    }
    const match = (asset.pricingTiers ?? []).find(
      (t) => (t.label ?? String(t.persons)) === state.tierKey,
    );
    if (!match) return null;
    return {
      label: match.label ?? `${match.persons} persone`,
      exclusive: false as const,
    };
  }, [state.tierKey, asset]);

  /* ── Extra lines (solo per la visualizzazione per-riga) ──────────────────────
   * Il totale extra è già incluso in pricing.total; qui servono i dettagli
   * per mostrare label, unit e quantità di ogni extra selezionato.
   */
  const extrasResolved = useMemo(
    () => resolveExcursionExtras(asset.extras ?? [], state.extras, people),
    [asset.extras, state.extras, people],
  );

  /* ── Destruttura valori dal helper ───────────────────────────────────────── */
  const {
    total,
    totalBeforeLM,
    lmPct,
    lmDiscount,
    baseClientTotal,
    unitClientPrice,
    taxes,
    customerOnline,
    customerOffline,
    hasSplit,
  } = pricing;

  return (
    <div data-cid="step-summary" className="flex flex-col gap-5 py-2">

      {/* ── Header: escursione / data / orario / partecipanti ─────────────── */}
      <div className="flex flex-col gap-1.5">
        {asset.title && (
          <h3 className="text-base font-bold text-slate-900 leading-snug">
            {asset.title}
          </h3>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
          {state.date && (
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={13} className="text-sky-500 shrink-0" />
              {fmtDate(state.date)}
            </span>
          )}
          {state.departureTime && (
            <span className="font-semibold text-slate-700">
              {state.departureTime}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5">
            <Users size={13} className="text-sky-500 shrink-0" />
            {people} {people === 1 ? 'partecipante' : 'partecipanti'}
          </span>
        </div>
      </div>

      <hr className="border-slate-100" />

      {/* ── Righe dettaglio prezzi (prezzi cliente con markup) ────────────── */}
      <div className="flex flex-col gap-3">

        {/* Base */}
        <div
          data-cid="step-summary-base"
          className="flex items-start justify-between gap-4"
        >
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-slate-800">
              {selectedTier
                ? selectedTier.label
                : asset.pricingMode === 'per_person'
                  ? `${people} ${people === 1 ? 'partecipante' : 'partecipanti'}`
                  : 'Tariffa gruppo'}
            </span>
            {/* Per_person: mostra la moltiplicazione esatta (arrotondamento per-unità) */}
            {!selectedTier && asset.pricingMode === 'per_person' && (
              <span className="text-xs text-slate-400">
                {people} × €{unitClientPrice}/pers
              </span>
            )}
          </div>
          <span className="text-sm font-bold text-slate-900 tabular-nums whitespace-nowrap">
            €{fmtCur(baseClientTotal)}
          </span>
        </div>

        {/* Extra servizio (markupabili) */}
        {extrasResolved.lines
          .filter((line) => line.kind !== 'tax')
          .map((line) => {
            const clientUnit  = applyMarkup(line.unit_price, markupRate);
            const clientTotal = clientUnit * line.quantity;
            return (
              <div
                key={line.extra_key}
                data-cid={`step-summary-extra-${line.extra_key}`}
                className="flex items-start justify-between gap-4"
              >
                <div className="flex flex-col min-w-0">
                  <span className="text-sm text-slate-700">{line.label}</span>
                  {line.price_type === 'per_person' && (
                    <span className="text-xs text-slate-400">
                      {line.quantity} × €{clientUnit}
                    </span>
                  )}
                </div>
                <span className="text-sm font-semibold text-slate-800 tabular-nums whitespace-nowrap">
                  €{fmtCur(clientTotal)}
                </span>
              </div>
            );
          })}

        {/* Last-minute sconto (se attivo) */}
        {lmDiscount > 0 && (
          <div className="flex items-center justify-between gap-4">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-black uppercase tracking-wider">
              Last Minute −{lmPct}%
            </span>
            <span className="text-sm font-semibold text-emerald-600 tabular-nums whitespace-nowrap">
              −€{fmtCur(lmDiscount)}
            </span>
          </div>
        )}

        {/* Extra tasse / fee (pass-through, no markup, saldo al porto) */}
        {extrasResolved.lines
          .filter((line) => line.kind === 'tax')
          .map((line) => {
            const taxTotal = line.unit_price * line.quantity;
            return (
              <div
                key={line.extra_key}
                data-cid={`step-summary-tax-${line.extra_key}`}
                className="flex items-start justify-between gap-4"
              >
                <div className="flex flex-col min-w-0">
                  <span className="text-sm text-slate-500">{line.label}</span>
                  <span className="text-xs text-slate-400">pagabile al porto</span>
                </div>
                <span className="text-sm text-slate-500 tabular-nums whitespace-nowrap">
                  €{fmtCur(taxTotal)}
                </span>
              </div>
            );
          })}
      </div>

      {/* ── Totale ────────────────────────────────────────────────────────── */}
      <div
        data-cid="step-summary-total"
        className="flex items-baseline justify-between gap-4 border-t border-slate-200 pt-4"
      >
        <span className="text-sm font-bold text-slate-800">Totale</span>
        <div className="flex items-baseline gap-2">
          {lmDiscount > 0 && (
            <span className="text-base font-bold text-slate-400 line-through tabular-nums">
              €{fmtCur(totalBeforeLM)}
            </span>
          )}
          <span className="text-2xl font-black text-slate-900 tabular-nums">
            €{fmtCur(total)}
          </span>
        </div>
      </div>

      {/* ── Nota tasse (se presenti e offline) ───────────────────────────── */}
      {taxes > 0 && (
        <p className="text-[11px] text-slate-400 -mt-2">
          Di cui €{fmtCur(taxes)} di tasse/fee da saldare al porto
        </p>
      )}

      {/* ── Acconto / saldo ───────────────────────────────────────────────── */}
      {hasSplit ? (
        <div
          data-cid="step-summary-deposit"
          className="rounded-xl border border-sky-200 bg-sky-50 p-4 flex flex-col gap-2.5"
        >
          <p className="text-[10px] font-bold uppercase tracking-wider text-sky-700">
            Modalità pagamento
          </p>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-700">
              Acconto online ora
              {asset.depositPercent != null && asset.depositPercent < 100
                ? ` (${asset.depositPercent}%)`
                : ''}
            </span>
            <span className="text-lg font-black text-sky-700 tabular-nums">
              €{fmtCur(customerOnline)}
            </span>
          </div>
          <div className="flex items-center justify-between border-t border-sky-100 pt-2">
            <span className="text-sm text-slate-500">Saldo a bordo</span>
            <span className="text-sm font-semibold text-slate-700 tabular-nums">
              €{fmtCur(customerOffline)}
            </span>
          </div>
        </div>
      ) : (
        <div
          data-cid="step-summary-deposit"
          className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 flex items-center justify-between gap-4"
        >
          <span className="text-sm font-semibold text-slate-700">
            Paghi online ora
          </span>
          <span className="text-lg font-black text-sky-700 tabular-nums">
            €{fmtCur(customerOnline)}
          </span>
        </div>
      )}

      {/* ── Cauzione — solo informativa, Fase 1 ──────────────────────────── */}
      {asset.requiresCardGuarantee && (
        <div
          data-cid="step-summary-guarantee"
          className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
          role="note"
          aria-label="Informazione cauzione"
        >
          <Info size={14} className="text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>Cauzione richiesta</strong> — preautorizzazione all&apos;imbarco.
            L&apos;importo non viene addebitato ora.
          </p>
        </div>
      )}
    </div>
  );
}
